import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Ticket, Sparkles, Trophy, Crown, Check, ArrowLeft } from "lucide-react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const PACK_VISUAL = {
  standard_single: {
    accent: "cyan",
    icon: Ticket,
    title: "Code Standard",
    subtitle: "1 participation standard",
  },
  discovery_pack: {
    accent: "fuchsia",
    icon: Sparkles,
    title: "Pack Découverte",
    subtitle: "5 codes standards + 1 offert",
    highlight: true,
  },
  golden_single: {
    accent: "amber",
    icon: Crown,
    title: "Golden Code",
    subtitle: "Accès aux Golden Quizz",
    golden: true,
  },
  champion_pack: {
    accent: "amber",
    icon: Crown,
    title: "Pack Champion",
    subtitle: "25 codes + 2 offerts + 1 Golden",
    best: true,
  },
};

export default function BuyCodes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [offers, setOffers] = useState(null);
  const [busy, setBusy] = useState(null);
  const [ageConfirmed, setAgeConfirmed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/offers");
        setOffers(data.offers);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  const buy = async (pack_type) => {
    if (!user) {
      try {
        sessionStorage.setItem("otra_buy_intent", pack_type);
      } catch (_e) {
        // ignore storage errors
      }
      toast.info("Connecte-toi pour acheter un code");
      navigate("/login?next=buy");
      return;
    }
    if (!ageConfirmed) {
      toast.error(
        "Confirme d'abord avoir 18 ans ou plus et accepter les CGU / CGV.",
      );
      return;
    }
    setBusy(pack_type);
    try {
      const { data } = await api.post("/payments/checkout/session", {
        origin_url: window.location.origin,
        pack_type,
        age_confirmed: true,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(null);
    }
  };

  return (
    <Layout>
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-16 sm:pb-24">
        <button
          data-testid="buy-back-home"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à l&apos;accueil
        </button>

        <div className="mb-10">
          <div className="inline-flex items-center gap-2 text-cyan-300 text-xs uppercase tracking-[0.3em] font-mono-stat mb-3">
            <Trophy className="w-3.5 h-3.5" />
            Acheter des codes
          </div>
          <h1
            data-testid="buy-page-title"
            className="font-display font-black uppercase tracking-tighter text-3xl sm:text-5xl leading-[0.95]"
          >
            Choisis ta{" "}
            <span className="text-cyan-400 neon-text-primary">formule</span>
          </h1>
          <p className="text-white/65 mt-4 max-w-xl text-sm sm:text-base">
            Tous les codes apparaissent dans <span className="text-cyan-300">Mes codes</span>{" "}
            après paiement. Un code Golden donne accès aux Golden Quizz exclusifs.
          </p>
        </div>

        {offers === null ? (
          <div className="panel rounded-2xl p-6 text-white/60 text-sm">Chargement…</div>
        ) : (
          <>
            <label
              data-testid="buy-age-confirm-wrap"
              htmlFor="buy-age-confirm"
              className={`flex items-start gap-3 mb-6 p-4 rounded-2xl border cursor-pointer transition-all ${
                ageConfirmed
                  ? "border-emerald-400/50 bg-emerald-400/5"
                  : "border-white/15 bg-white/5 hover:border-cyan-400/40"
              }`}
            >
              <input
                id="buy-age-confirm"
                data-testid="buy-age-confirm-checkbox"
                type="checkbox"
                checked={ageConfirmed}
                onChange={(e) => setAgeConfirmed(e.target.checked)}
                className="mt-1 w-5 h-5 accent-cyan-400 shrink-0 cursor-pointer"
              />
              <div className="text-sm text-white/80 leading-relaxed">
                Je confirme avoir{" "}
                <span className="text-white font-semibold">18 ans ou plus</span> et
                j&apos;accepte les{" "}
                <Link
                  to="/cgu"
                  target="_blank"
                  rel="noopener"
                  className="text-cyan-300 underline hover:text-cyan-200"
                  data-testid="buy-age-confirm-cgu-link"
                >
                  CGU
                </Link>{" "}
                et les{" "}
                <Link
                  to="/cgv"
                  target="_blank"
                  rel="noopener"
                  className="text-cyan-300 underline hover:text-cyan-200"
                  data-testid="buy-age-confirm-cgv-link"
                >
                  CGV
                </Link>
                . L&apos;achat de codes est strictement réservé aux personnes majeures.
              </div>
            </label>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {offers.map((o) => {
              const visual = PACK_VISUAL[o.pack_type] || PACK_VISUAL.standard_single;
              const Icon = visual.icon;
              const isBusy = busy === o.pack_type;
              const isPrelaunchBoost = o.pack_type === "discovery_pack" && o.prelaunch;
              return (
                <div
                  key={o.pack_type}
                  data-testid={`buy-pack-${o.pack_type}`}
                  className={`relative rounded-2xl p-5 sm:p-6 border ${
                    visual.best
                      ? "border-amber-400/60 bg-gradient-to-b from-amber-400/10 to-amber-400/0 shadow-[0_0_45px_rgba(251,191,36,0.18)]"
                      : isPrelaunchBoost
                      ? "border-fuchsia-400/60 bg-gradient-to-b from-fuchsia-400/15 via-amber-400/5 to-transparent shadow-[0_0_40px_rgba(217,70,239,0.22)]"
                      : visual.golden
                      ? "border-amber-400/50 bg-gradient-to-b from-amber-400/8 to-amber-400/0 shadow-[0_0_30px_rgba(251,191,36,0.12)]"
                      : visual.highlight
                      ? "border-fuchsia-400/50 bg-gradient-to-b from-fuchsia-400/10 to-fuchsia-400/0"
                      : "border-white/10 panel"
                  }`}
                >
                  {visual.best && (
                    <span
                      data-testid="buy-pack-best-badge"
                      className="absolute -top-3 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-400 text-black text-[10px] uppercase tracking-widest font-display font-bold"
                    >
                      <Crown className="w-3 h-3" />
                      Meilleur deal
                    </span>
                  )}
                  {isPrelaunchBoost && (
                    <span
                      data-testid="buy-pack-prelaunch-badge"
                      className="absolute -top-3 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-fuchsia-500 text-white text-[10px] uppercase tracking-widest font-display font-bold"
                    >
                      <Sparkles className="w-3 h-3" />
                      Offre limitée
                    </span>
                  )}
                  {visual.golden && !visual.best && (
                    <span
                      data-testid="buy-pack-golden-badge"
                      className="absolute -top-3 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-400/90 text-black text-[10px] uppercase tracking-widest font-display font-bold"
                    >
                      <Crown className="w-3 h-3" />
                      Golden
                    </span>
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        visual.best || visual.golden
                          ? "bg-amber-400/20 text-amber-300"
                          : visual.highlight
                          ? "bg-fuchsia-400/15 text-fuchsia-300"
                          : "bg-cyan-400/15 text-cyan-300"
                      }`}
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="font-display font-bold uppercase tracking-tight text-lg">
                        {isPrelaunchBoost ? "Pack Découverte" : visual.title}
                      </div>
                      <div className="text-[11px] uppercase tracking-widest text-white/45 font-mono-stat">
                        {isPrelaunchBoost ? "Offre pré-ouverture" : visual.subtitle}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="font-mono-stat text-4xl sm:text-5xl font-black">
                      {o.amount_eur.toFixed(0)}
                    </span>
                    <span className="text-white/60 text-lg">€</span>
                  </div>

                  <ul className="space-y-2 text-sm text-white/80 mb-5">
                    {o.paid_standard > 0 && (
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" />
                        <span>
                          {o.paid_standard}{" "}
                          <span className="text-white/55">
                            {o.paid_standard > 1 ? "codes standards" : "code standard"}
                          </span>
                        </span>
                      </li>
                    )}
                    {o.paid_golden > 0 && (
                      <li className="flex items-start gap-2">
                        <Crown className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                        <span>
                          {o.paid_golden}{" "}
                          <span className="text-amber-200/80">
                            {o.paid_golden > 1 ? "Golden Codes" : "Golden Code"}
                          </span>
                        </span>
                      </li>
                    )}
                    {o.bonus_standard > 0 && (
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-fuchsia-300 shrink-0 mt-0.5" />
                        <span>
                          <span className="text-fuchsia-200 font-semibold">
                            +{o.bonus_standard}
                          </span>{" "}
                          <span className="text-white/55">offert(s)</span>
                        </span>
                      </li>
                    )}
                    {o.bonus_golden > 0 && (
                      <li className="flex items-start gap-2">
                        <Crown className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                        <span>
                          <span className="text-amber-200 font-semibold">
                            +{o.bonus_golden}
                          </span>{" "}
                          <span className="text-white/55">Golden Code</span>
                        </span>
                      </li>
                    )}
                    <li className="flex items-start gap-2 pt-1 border-t border-white/5 mt-2">
                      <Sparkles className="w-4 h-4 text-white/40 shrink-0 mt-0.5" />
                      <span className="text-white/70">
                        Total :{" "}
                        <span className="font-mono-stat text-white">{o.total_codes}</span>{" "}
                        code(s)
                      </span>
                    </li>
                  </ul>

                  <button
                    data-testid={`buy-pack-${o.pack_type}-cta`}
                    onClick={() => buy(o.pack_type)}
                    disabled={isBusy || !ageConfirmed}
                    title={
                      !ageConfirmed
                        ? "Confirme d'abord avoir 18 ans ou plus et accepter les CGU / CGV."
                        : undefined
                    }
                    className={`w-full py-3 rounded-xl font-display font-bold uppercase tracking-wider text-sm active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                      visual.best || visual.golden
                        ? "bg-amber-400 hover:bg-amber-300 text-black"
                        : visual.highlight
                        ? "bg-fuchsia-500 hover:bg-fuchsia-400 text-white"
                        : "bg-cyan-400 hover:bg-cyan-300 text-black"
                    }`}
                  >
                    {isBusy ? "Redirection…" : "Choisir"}
                  </button>
                </div>
              );
            })}
          </div>
          </>
        )}

        <div className="mt-10 p-5 rounded-2xl border border-amber-400/30 bg-amber-400/5">
          <div className="flex items-start gap-3">
            <Crown className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
            <div className="text-sm text-white/75 leading-relaxed">
              <span className="text-amber-200 font-semibold">Golden Quizz</span> : une
              catégorie de défis exclusive, accessible uniquement avec un{" "}
              <span className="text-amber-200 font-semibold">Golden Code</span>. Tu en obtiens un
              avec le Pack Champion à 25 € ou en achetant un Golden Code seul à 5 €.
              Les codes standards ne fonctionnent pas sur ces défis (et inversement).
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
